import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  TextField,
  IconButton,
  Fab,
  Chip,
  InputAdornment,
  Tooltip,
  useTheme,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  NotificationsActive as AlertIcon,
  SentimentVerySatisfied as HappyIcon,
  SentimentVeryDissatisfied as SadIcon,
  CalendarMonth as CalendarIcon,
} from "@mui/icons-material";
import { useAppSelector, useAppDispatch } from "@hooks/redux";
import {
  fetchApiariesAndGroups,
  setFilters,
  clearFilters,
} from "@store/slices/locationsSlice";
import ApiaryPreview from "@components/apiaries/ApiaryPreview";
import { Hive, HiveFilters } from "@types";

const INSPECTION_OVERDUE_DAYS = 14;

const isHiveOverdue = (hive: Hive) => {
  const raw = hive.last_inspection_date_locale_date || hive.last_inspection_date;
  if (!raw) return true;
  const inspected = new Date(raw);
  if (Number.isNaN(inspected.getTime())) return true;
  return (Date.now() - inspected.getTime()) / 86400000 > INSPECTION_OVERDUE_DAYS;
};

const hiveMatchesFilters = (hive: Hive, filters: HiveFilters) => {
  const search = (filters.search || "").trim().toLowerCase();
  if (search) {
    const haystack = `${hive.name} ${hive.label || ""} ${hive.location_name || ""}`.toLowerCase();
    if (!haystack.includes(search)) return false;
  }

  if (filters.attention && !hive.attention) return false;
  if (filters.overdue && !isHiveOverdue(hive)) return false;

  if (filters.reminder) {
    if (!hive.reminder_date) return false;
    const due = new Date(hive.reminder_date);
    if (Number.isNaN(due.getTime()) || due.getTime() > Date.now()) return false;
  }

  const impressions = filters.impression || [];
  if (impressions.length > 0) {
    if (hive.impression == null) return false;
    const wantsSad = impressions.includes(1);
    const wantsHappy = impressions.includes(5);
    const isSad = hive.impression <= 2;
    const isHappy = hive.impression >= 4;
    if (!((wantsSad && isSad) || (wantsHappy && isHappy))) return false;
  }

  return true;
};

const Home: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { apiaries, groups, filters, loadingState, error } =
    useAppSelector((state) => state.locations);

  const [localSearch, setLocalSearch] = useState(filters.search || "");
  const [viewSize, setViewSize] = useState<'xs' | 'm' | 'xl'>('m');

  const ownedHiveIds = useMemo(
    () =>
      new Set(
        apiaries.flatMap((apiary) => (apiary.hives || []).map((hive) => hive.id)),
      ),
    [apiaries],
  );

  // Groups on Home are only for hives shared with you that you do not already
  // own. An owned hive that is also in a group stays under its apiary once.
  const incomingGroups = useMemo(
    () =>
      groups
        .map((group) => ({
          ...group,
          hives: (group.hives || []).filter((hive) => !ownedHiveIds.has(hive.id)),
        }))
        .filter((group) => group.hives.length > 0),
    [groups, ownedHiveIds],
  );

  const sharesByApiaryId = useMemo(() => {
    const map: Record<number, string[]> = {};
    apiaries.forEach((apiary) => {
      const hiveIds = new Set((apiary.hives || []).map((hive) => hive.id));
      const names = groups
        .filter((group) =>
          (group.hives || []).some((hive) => hiveIds.has(hive.id)),
        )
        .map((group) => group.name);
      if (names.length > 0) {
        map[apiary.id] = names;
      }
    });
    return map;
  }, [apiaries, groups]);

  useEffect(() => {
    if (loadingState === "idle") {
      dispatch(fetchApiariesAndGroups());
    }
  }, [dispatch, loadingState]);

  const visibleApiaries = useMemo(
    () =>
      apiaries
        .map((apiary) => ({
          ...apiary,
          hives: (apiary.hives || []).filter((hive) =>
            hiveMatchesFilters(hive, filters),
          ),
        }))
        .filter((apiary) => apiary.hives.length > 0),
    [apiaries, filters],
  );

  const visibleIncomingGroups = useMemo(
    () =>
      incomingGroups
        .map((group) => ({
          ...group,
          hives: (group.hives || []).filter((hive) =>
            hiveMatchesFilters(hive, filters),
          ),
        }))
        .filter((group) => group.hives.length > 0),
    [incomingGroups, filters],
  );

  const hasActiveFilters = Boolean(
    (filters.search && filters.search.trim()) ||
      filters.attention ||
      filters.overdue ||
      filters.reminder ||
      (filters.impression && filters.impression.length > 0),
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLocalSearch(value);
    dispatch(setFilters({ search: value || undefined }));
  };

  const handleClearSearch = () => {
    setLocalSearch("");
    dispatch(setFilters({ search: undefined }));
  };

  const handleFilterToggle = (filterType: keyof HiveFilters, value?: any) => {
    switch (filterType) {
      case "attention":
        dispatch(setFilters({ attention: !filters.attention }));
        break;
      case "reminder":
        dispatch(setFilters({ reminder: !filters.reminder }));
        break;
      case "group":
        const nextGroupValue =
          filters.group === "off"
            ? "owned"
            : filters.group === "owned"
              ? "shared"
              : "off";
        dispatch(setFilters({ group: nextGroupValue }));
        break;
      case "impression":
        const currentImpressions = filters.impression || [];
        const newImpressions = currentImpressions.includes(value)
          ? currentImpressions.filter((i) => i !== value)
          : [...currentImpressions, value];
        dispatch(setFilters({ impression: newImpressions }));
        break;
      case "overdue":
        dispatch(setFilters({ overdue: !filters.overdue }));
        break;
    }
  };

  const handleClearAllFilters = () => {
    setLocalSearch("");
    dispatch(clearFilters());
  };

  const handleCreateApiary = () => {
    navigate("/apiaries/create");
  };

  const handleCreateHive = () => {
    navigate("/hives/create");
  };

  const handleViewSizeChange = (
    event: React.MouseEvent<HTMLElement>,
    newSize: 'xs' | 'm' | 'xl' | null,
  ) => {
    if (newSize !== null) {
      setViewSize(newSize);
    }
  };

  if (loadingState === "pending") {
    return (
      <Container>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={50} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography color="error" variant="h6">
            {error}
          </Typography>
        </Box>
      </Container>
    );
  }

  const showPlaceholder = apiaries.length === 0 && groups.length === 0;

  return (
    <Box>
      {/* Filter Bar - Clean and Compact */}
      {!showPlaceholder && (
        <Box
          sx={{
            backgroundColor: "#fff",
            borderBottom: "1px solid #e0e0e0",
            py: { xs: 0.75, md: 1.5 },
            mb: 2,
          }}
        >
          <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 1.5, md: 3 } }}>
            <Box sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: { xs: 0.5, sm: 1, md: 2 },
              flexWrap: 'nowrap',
            }}>
              {/* Left Side: Search and Filter Icons Grouped Together */}
              <Box sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 0.25, sm: 0.5, md: 1 },
                flex: 1,
                minWidth: 0,
              }}>
                {/* Search Field */}
                <TextField
                  value={localSearch}
                  onChange={handleSearchChange}
                  placeholder="Search..."
                  size="small"
                  sx={{
                    width: { xs: 100, sm: 140, md: 200, lg: 240 },
                    flexShrink: 1,
                    minWidth: { xs: 80, sm: 120 },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fafafa",
                      height: { xs: 32, md: 36 },
                      fontSize: { xs: '0.75rem', md: '0.875rem' },
                    },
                    "& .MuiInputBase-input": {
                      padding: { xs: '6px 8px', md: '8px 12px' },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: { xs: '1rem', md: '1.25rem' }, color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    endAdornment: localSearch && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={handleClearSearch} sx={{ p: 0.25 }}>
                          <ClearIcon sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Tooltip title="Needs attention">
                  <IconButton
                    size="small"
                    onClick={() => handleFilterToggle("attention")}
                    aria-label="Needs attention"
                    sx={{
                      width: { xs: 36, sm: 36, md: 40 },
                      height: { xs: 36, sm: 36, md: 40 },
                      minWidth: { xs: 36, sm: 36, md: 40 },
                      p: 0,
                      color: filters.attention
                        ? theme.palette.error.main
                        : theme.palette.grey[400],
                      bgcolor: filters.attention
                        ? 'rgba(244, 67, 54, 0.08)'
                        : 'transparent',
                      '&:hover': {
                        bgcolor: filters.attention
                          ? 'rgba(244, 67, 54, 0.12)'
                          : 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <AlertIcon sx={{ fontSize: { xs: '1.15rem', md: '1.35rem' } }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Inspection overdue (14+ days or never)">
                  <IconButton
                    size="small"
                    onClick={() => handleFilterToggle("overdue")}
                    aria-label="Inspection overdue"
                    sx={{
                      width: { xs: 36, sm: 36, md: 40 },
                      height: { xs: 36, sm: 36, md: 40 },
                      minWidth: { xs: 36, sm: 36, md: 40 },
                      p: 0,
                      color: filters.overdue
                        ? theme.palette.warning.dark
                        : theme.palette.grey[400],
                      bgcolor: filters.overdue
                        ? 'rgba(237, 108, 2, 0.08)'
                        : 'transparent',
                      '&:hover': {
                        bgcolor: filters.overdue
                          ? 'rgba(237, 108, 2, 0.12)'
                          : 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <CalendarIcon sx={{ fontSize: { xs: '1.15rem', md: '1.35rem' } }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Last inspection was poor">
                  <IconButton
                    size="small"
                    onClick={() => handleFilterToggle("impression", 1)}
                    aria-label="Poor last inspection"
                    sx={{
                      width: { xs: 36, sm: 36, md: 40 },
                      height: { xs: 36, sm: 36, md: 40 },
                      minWidth: { xs: 36, sm: 36, md: 40 },
                      p: 0,
                      color: filters.impression?.includes(1)
                        ? theme.palette.error.main
                        : theme.palette.grey[400],
                      bgcolor: filters.impression?.includes(1)
                        ? 'rgba(244, 67, 54, 0.08)'
                        : 'transparent',
                      '&:hover': {
                        bgcolor: filters.impression?.includes(1)
                          ? 'rgba(244, 67, 54, 0.12)'
                          : 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <SadIcon sx={{ fontSize: { xs: '1.15rem', md: '1.35rem' } }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Last inspection was good">
                  <IconButton
                    size="small"
                    onClick={() => handleFilterToggle("impression", 5)}
                    aria-label="Good last inspection"
                    sx={{
                      width: { xs: 36, sm: 36, md: 40 },
                      height: { xs: 36, sm: 36, md: 40 },
                      minWidth: { xs: 36, sm: 36, md: 40 },
                      p: 0,
                      color: filters.impression?.includes(5)
                        ? theme.palette.success.main
                        : theme.palette.grey[400],
                      bgcolor: filters.impression?.includes(5)
                        ? 'rgba(76, 175, 80, 0.08)'
                        : 'transparent',
                      '&:hover': {
                        bgcolor: filters.impression?.includes(5)
                          ? 'rgba(76, 175, 80, 0.12)'
                          : 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <HappyIcon sx={{ fontSize: { xs: '1.15rem', md: '1.35rem' } }} />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* View Size Toggle */}
              <ToggleButtonGroup
                value={viewSize}
                exclusive
                onChange={handleViewSizeChange}
                size="small"
                sx={{
                  height: { xs: 28, md: 32 },
                  flexShrink: 0,
                  '& .MuiToggleButton-root': {
                    px: { xs: 0.5, sm: 0.75, md: 1 },
                    py: { xs: 0.25, md: 0.5 },
                    fontSize: { xs: '0.65rem', md: '0.7rem' },
                    fontWeight: 600,
                    color: 'text.secondary',
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    minWidth: { xs: 24, sm: 28, md: 32 },
                    '&.Mui-selected': {
                      color: 'primary.main',
                      bgcolor: 'rgba(248, 177, 51, 0.1)',
                      borderColor: 'primary.main',
                      '&:hover': {
                        bgcolor: 'rgba(248, 177, 51, 0.15)',
                      }
                    }
                  }
                }}
              >
                <ToggleButton value="xs">XS</ToggleButton>
                <ToggleButton value="m">M</ToggleButton>
                <ToggleButton value="xl">XL</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {hasActiveFilters && (
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1 }}>
                {filters.search?.trim() && (
                  <Chip size="small" label={`Search: ${filters.search.trim()}`} onDelete={handleClearSearch} />
                )}
                {filters.attention && (
                  <Chip
                    size="small"
                    label="Needs attention"
                    onDelete={() => dispatch(setFilters({ attention: false }))}
                  />
                )}
                {filters.overdue && (
                  <Chip
                    size="small"
                    label="Inspection overdue"
                    onDelete={() => dispatch(setFilters({ overdue: false }))}
                  />
                )}
                {filters.impression?.includes(1) && (
                  <Chip
                    size="small"
                    label="Poor last check"
                    onDelete={() => handleFilterToggle("impression", 1)}
                  />
                )}
                {filters.impression?.includes(5) && (
                  <Chip
                    size="small"
                    label="Good last check"
                    onDelete={() => handleFilterToggle("impression", 5)}
                  />
                )}
                <Chip
                  size="small"
                  label="Clear filters"
                  onClick={handleClearAllFilters}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            )}
          </Container>
        </Box>
      )}

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ pt: showPlaceholder ? 2 : 0, pb: 4, px: { xs: 2, sm: 3 } }}>
        {showPlaceholder ? (
          // Placeholder for new users
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h4" gutterBottom color="primary">
              Welcome to Smart Nyuki!
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
              Start by creating your first apiary to organize your hives.
            </Typography>
            <Fab
              color="primary"
              variant="extended"
              onClick={handleCreateApiary}
              sx={{ mb: 2 }}
            >
              <AddIcon sx={{ mr: 1 }} />
              Create First Apiary
            </Fab>
          </Box>
        ) : (
          <>
            {visibleApiaries.map((apiary) => (
              <ApiaryPreview
                key={apiary.id}
                apiary={apiary}
                viewSize={viewSize}
                sharedWith={sharesByApiaryId[apiary.id]}
              />
            ))}

            {visibleIncomingGroups.map((group) => (
              <ApiaryPreview
                key={`group-${group.id}`}
                apiary={group}
                isGroup
                viewSize={viewSize}
              />
            ))}

            {visibleApiaries.length === 0 &&
              visibleIncomingGroups.length === 0 &&
              (apiaries.length > 0 || groups.length > 0) && (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <Typography variant="h6" color="textSecondary">
                    No hives match your current filters
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mb: 2 }}
                  >
                    Try adjusting your search or filters
                  </Typography>
                  <Chip
                    label="Clear Filters"
                    onClick={handleClearAllFilters}
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              )}

            {/* Floating Action Button */}
            <Fab
              size="medium"
              color="primary"
              sx={{
                position: "fixed",
                bottom: { xs: 70, md: 20 },
                right: { xs: 16, md: 24 },
                width: { xs: 48, md: 56 },
                height: { xs: 48, md: 56 },
                zIndex: 999,
                opacity: 0.9,
                '&:hover': {
                  opacity: 1,
                }
              }}
              onClick={handleCreateHive}
            >
              <AddIcon sx={{ fontSize: { xs: '1.3rem', md: '1.5rem' } }} />
            </Fab>
          </>
        )}
      </Container>
    </Box>
  );
};

export default Home;
